import { gql, useLazyQuery } from "@apollo/client"
import { useCallback } from "react"
import { Rate, ExchangeRateTypeEnum, QueryExchangeRateArgs } from "@oysterhr/schema"

const GET_EXCHANGE_RATE = gql`
  query ExchangeRate($from: String!, $to: String!, $type: ExchangeRateTypeEnum!) {
    exchangeRate(from: $from, to: $to, type: $type) {
      id
      from
      to
      rate
    }
  }
`

export const useOysterCurrencyConversion = (type: ExchangeRateTypeEnum) => {
  const [fetchConversion] = useLazyQuery<{ exchangeRate: Rate }, QueryExchangeRateArgs>(
    GET_EXCHANGE_RATE,
    { fetchPolicy: "cache-first" },
  )

  const getConversion = useCallback(
    async (fromCurrency: string, toCurrency: string) => {
      const { data } = await fetchConversion({
        variables: {
          type,
          from: fromCurrency,
          to: toCurrency,
        },
      })

      return data?.exchangeRate
    },
    [fetchConversion, type],
  )

  /**
   * The inverse of the conversion rate needs to be calculated here as we're working out
   * the cost of buying X in the target currency.
   * So, instead of calculating how many USD you can buy for GBP, we need to calculate
   * how many GBP do I need to buy a specific amount in USD.
   *
   * e.g. I want to pay a bonus of £100. How much is this in USD?
   */
  const getReverseFxConversionRate = useCallback(
    async (from: string, to: string) => {
      if (from === to) {
        return 1
      } else {
        const exchangeRate = await getConversion(to, from)
        if (!exchangeRate) {
          return 1
        }
        return 1 / exchangeRate.rate
      }
    },
    [getConversion],
  )

  return { getConversion, getReverseFxConversionRate }
}
